import { useCallback, useContext, useState } from "react";
import { toast } from "react-toastify";
import router from "next/router";
import { Button } from "components/atoms/Button";
import { ButtonType } from "components/atoms/Button/types";
import { Modal } from "components/atoms/Modal/index";
import Spinner from "components/atoms/Spinner";
import { editImage, poll, pollEditImage } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, EditType, UserPremiumType } from "types";
import { Span, Title } from "utils/styling/typography.styled";
import { ButtonContainer, FormGroup, GoldCost, Label, ModalContainer, PremiumFormGroup, TextArea, ToggleSwitch } from "./EditModal.styled";
interface EditModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit?: (editRequest: string, isPremium: boolean, editType: string) => void;
  imageId: string;
}
export const EditModal: React.FC<EditModalProps> = ({
  isOpen,
  onCancel,
  onSubmit,
  imageId
}) => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [description, setDescription] = useState<string>("");
  const [isPremium, setIsPremium] = useState<boolean>(false);
  const [loading, setLoading] = useState(false); // Loading state

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
  };
  const handlePremiumToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsPremium(e.target.checked);
  };
  const handleEditSubmit = useCallback(async (editRequest: string, isPremium: boolean, imageId: string, editType: string, negativePrompt?: string, cfgScale?: number, numDetections?: number) => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    if (state.userPremiumLevel === UserPremiumType.NONE && state.gold < 1) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    } else if (state.gold < 1) {
      dispatch({
        type: AppReducerActionType.SHOW_PRICING_TABLE,
        payload: {
          ...state,
          showPricingTable: true
        }
      });
      return;
    }
    const formData = new FormData();
    formData.append("editRequest", editRequest);
    formData.append("imageId", imageId);
    formData.append("premium", isPremium.toString());
    formData.append("editType", editType);
    if (negativePrompt) {
      formData.append("negativePrompt", negativePrompt);
    }
    if (cfgScale) {
      formData.append("cfgScale", cfgScale.toString());
    }
    if (numDetections) {
      formData.append("numDetections", numDetections.toString());
    }
    setLoading(true);
    const {
      requestId,
      error
    } = await editImage(formData);
    if (error) {
      toast.error("An error occurred while submitting the edit request.");
      return;
    }
    if (requestId) {
      const {
        image,
        error
      } = await poll(async () => {
        const result = await pollEditImage(requestId);
        if (result.error && typeof result.error === "object") {
          console.log("Error details:", result.error);
        }
        return result;
      }, ({
        image,
        error
      }) => {
        // Continue polling only if there is no image and no critical error
        if (!!image) return false; // Stop polling when an image is retrieved
        if (typeof error === "string") return false;
        if (typeof error === "object") return false;
        return true; // Continue polling otherwise
      }, 5000);
      if (error) {
        if (typeof error === "object") {
          toast.error(error.message || "An unknown error occurred while generating the image and your gold has been refunded. Please try again.");
          dispatch({
            type: AppReducerActionType.SET_GOLD,
            payload: {
              ...state,
              gold: state.gold + 1
            }
          });
        } else if (typeof error === "string") {
          toast.error("The request timed out. Please try again.");
          dispatch({
            type: AppReducerActionType.SET_GOLD,
            payload: {
              ...state,
              gold: state.gold + 1
            }
          });
        }
      }
      if (image && image.length > 0) {
        const newImage = Array.isArray(image) ? image[0] : image;

        // Redirect to the new image's page
        router.push(`/images/${newImage.id}`);
        dispatch({
          type: AppReducerActionType.DEC_GOLD,
          payload: state
        });
      }
    }
    setLoading(false);
  }, [dispatch, state]);
  const handleSubmit = async () => {
    if (onSubmit) {
      onSubmit(description, isPremium, EditType.IMAGE);
    } else {
      // Otherwise, use the internal handleEditSubmit function
      await handleEditSubmit(description, isPremium, imageId, EditType.IMAGE);
      onCancel();
    }
  };
  return <Modal open={isOpen} handleBackdropClick={onCancel} disableBackdropClick data-sentry-element="Modal" data-sentry-component="EditModal" data-sentry-source-file="index.tsx">
            <ModalContainer data-sentry-element="ModalContainer" data-sentry-source-file="index.tsx">
                <Title data-sentry-element="Title" data-sentry-source-file="index.tsx">Edit Image</Title>
                <hr />
                <FormGroup data-sentry-element="FormGroup" data-sentry-source-file="index.tsx">
                    <Label htmlFor="description" data-sentry-element="Label" data-sentry-source-file="index.tsx">Edit Description:</Label>
                    <TextArea id="description" name="description" value={description} onChange={handleDescriptionChange} placeholder="Enter a detailed description of the changes you want to make to the image." data-sentry-element="TextArea" data-sentry-source-file="index.tsx" />
                </FormGroup>
                <PremiumFormGroup data-sentry-element="PremiumFormGroup" data-sentry-source-file="index.tsx">
                    <Label htmlFor="premiumToggle" data-sentry-element="Label" data-sentry-source-file="index.tsx">Premium Edit:</Label>
                    <ToggleSwitch data-sentry-element="ToggleSwitch" data-sentry-source-file="index.tsx">
                        <input id="premiumToggle" type="checkbox" checked={isPremium && !!state.gold} onChange={handlePremiumToggle} disabled={!state.gold} />
                        <span className="slider round"></span>
                    </ToggleSwitch>
                </PremiumFormGroup>
                <GoldCost data-sentry-element="GoldCost" data-sentry-source-file="index.tsx">Gold Cost: 1 Gold</GoldCost>
                <ButtonContainer data-sentry-element="ButtonContainer" data-sentry-source-file="index.tsx">
                    <Button type="button" variant={ButtonType.SUCCESS} onClick={handleSubmit} disabled={loading} // Disable the button while loading
        data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        {loading ? <Spinner size={24} color="inherit" /> // Show spinner when loading
          : <Span>Submit</Span>}{" "}
                    </Button>
                    <Button type="button" variant={ButtonType.PRIMARY} onClick={onCancel} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        <Span data-sentry-element="Span" data-sentry-source-file="index.tsx">Cancel</Span>
                    </Button>
                </ButtonContainer>
            </ModalContainer>
        </Modal>;
};